import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import LandingPage from "./pages/LandingPage";
import PublicLanding from "./pages/PublicLanding";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetails from "./pages/ProjectDetails";
import Budgets from "./pages/Budgets";
import AdminPanel from "./pages/AdminPanel";
import Profile from "./pages/Profile";
import Community from "./pages/Community";
import Tutoriais from "./pages/Tutoriais";
import FinanceDashboard from "./pages/FinanceDashboard";
import ActivityLog from "./pages/ActivityLog";
import Tools from "./pages/Tools";
import Historico from "./pages/Historico";
import BudgetRequests from "./pages/BudgetRequests";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ConfirmProvider } from "./components/ConfirmDialog";

const ProtectedRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/equipe" replace />;
  }

  // Block pending users
  if (user.approval_status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 p-6">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md w-full border border-amber-500/30 text-center" data-testid="pending-approval-page">
          <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="h-10 w-10 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Aguardando Aprovação</h2>
          <p className="text-slate-400 mb-6">Sua solicitação de acesso foi enviada. Um administrador precisa aprovar seu cadastro antes que você possa acessar o sistema.</p>
          <p className="text-slate-500 text-sm mb-6">Logado como: <span className="text-white">{user.email}</span></p>
          <button onClick={logout} className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg transition-colors">Sair</button>
        </div>
      </div>
    );
  }

  // Block denied users
  if (user.approval_status === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 p-6">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md w-full border border-red-500/30 text-center" data-testid="denied-access-page">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="h-10 w-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Acesso Negado</h2>
          <p className="text-slate-400 mb-6">Sua solicitação de acesso foi negada por um administrador. Entre em contato com a equipe caso acredite que seja um erro.</p>
          <button onClick={logout} className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg transition-colors">Sair</button>
        </div>
      </div>
    );
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicLanding />} />
      <Route path="/equipe" element={<LandingPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
      <Route path="/budgets" element={<ProtectedRoute><Budgets /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/tutoriais" element={<ProtectedRoute><Tutoriais /></ProtectedRoute>} />
      <Route path="/finance" element={<ProtectedRoute><FinanceDashboard /></ProtectedRoute>} />
      <Route path="/activity-log" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
      <Route path="/historico" element={<ProtectedRoute><Historico /></ProtectedRoute>} />
      <Route path="/orcamentos" element={<ProtectedRoute><BudgetRequests /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ConfirmProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;